/**
 * 牌型描述。见 docs/ARCHITECTURE.md §4.3。
 *
 * **UI 不得自己拼字符串**，一律调 `formatHand`。新手辅助、回放、统计三处文案
 * 必须一致，服务端 `HandResult.handLabel` 也由此函数产出——一旦有人在组件里
 * 顺手拼了一个「两对 AK」，回放和统计里就会同时出现两种写法，而且没人会发现。
 *
 * `describeHand` 先把 HandRank 拆成结构化的 `HandDescriptor`（category / i18n key /
 * primary / kickers / isWheel / isRoyal），再由 format 层套模板。这样想换语言、
 * 想在 UI 里给 primary 和 kicker 用不同字号，都不必重新解析牌力编码。
 */
import { HandCategory, type HandRank } from './types.js'
import type { RankIndex } from './cards.js'
import { isShortDeckValue } from './evaluator.js'

export type Locale = 'zh-CN' | 'en'

export interface HandDescriptor {
  category: HandCategory
  /** i18n key，如 'hand.twoPair'；皇家同花顺用 'hand.royalFlush' */
  key: string
  /** 决定牌型的点数（葫芦是 [三条, 对子]，两对是 [大对, 小对]） */
  primary: readonly RankIndex[]
  /** 只用于破平的点数 */
  kickers: readonly RankIndex[]
  isWheel: boolean
  isRoyal: boolean
}

/**
 * 展示用点数标签。这里 10 写作 '10' 而不是 cards.ts 的 'T'：
 * `Card` 串是给机器读的（'T' 保证定长 2），这张表是给人读的。
 */
const RANK_LABELS: readonly string[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A']

const CATEGORY_KEYS: readonly string[] = [
  'hand.highCard',
  'hand.pair',
  'hand.twoPair',
  'hand.trips',
  'hand.straight',
  'hand.flush',
  'hand.fullHouse',
  'hand.quads',
  'hand.straightFlush',
]

export const ROYAL_FLUSH_KEY = 'hand.royalFlush'

/** 每类里有几个 slot 属于 primary，其余归 kickers */
const PRIMARY_COUNTS: readonly number[] = [
  1, // HighCard：最大的那张是牌型本身，其余 4 张是踢脚
  1, // Pair
  2, // TwoPair
  1, // Trips
  1, // Straight
  1, // Flush：只有最大牌参与描述，其余 4 张仅用于破平
  2, // FullHouse
  1, // Quads
  1, // StraightFlush
]

/**
 * 轮子：A 当最小牌的顺子/同花顺。标准是 A-2-3-4-5（5 高，rank 3）；
 * 短牌是 A-6-7-8-9（9 高，rank 7）——玩法从牌力值的标记位读，不用另传参数。
 */
function isWheelRank(rank: HandRank): boolean {
  return (
    (rank.category === HandCategory.Straight || rank.category === HandCategory.StraightFlush) &&
    rank.ranks[0] === (isShortDeckValue(rank.value) ? 7 : 3)
  )
}

export function describeHand(rank: HandRank): HandDescriptor {
  const isRoyal = rank.category === HandCategory.StraightFlush && rank.ranks[0] === 12
  const primaryCount = PRIMARY_COUNTS[rank.category]!
  return {
    category: rank.category,
    key: isRoyal ? ROYAL_FLUSH_KEY : CATEGORY_KEYS[rank.category]!,
    primary: rank.ranks.slice(0, primaryCount),
    kickers: rank.ranks.slice(primaryCount),
    isWheel: isWheelRank(rank),
    isRoyal,
  }
}

const label = (r: RankIndex): string => RANK_LABELS[r] ?? '?'
const labels = (rs: readonly RankIndex[]): string => rs.map(label).join(' ')

/**
 * 不含踢脚的短文案。牌桌上位置紧张时用它；
 * `formatHand` 就是它再接一段踢脚子句，两者不会走味。
 */
export function formatHandShort(rank: HandRank, locale: Locale = 'zh-CN'): string {
  const d = describeHand(rank)
  const p = d.primary
  const zh = locale !== 'en'

  switch (d.category) {
    case HandCategory.HighCard:
      return zh ? `高牌${label(p[0]!)}` : `High card ${label(p[0]!)}`
    case HandCategory.Pair:
      return zh ? `一对${label(p[0]!)}` : `Pair of ${label(p[0]!)}`
    case HandCategory.TwoPair:
      return zh
        ? `两对，${label(p[0]!)} 和 ${label(p[1]!)}`
        : `Two pair, ${label(p[0]!)} and ${label(p[1]!)}`
    case HandCategory.Trips:
      return zh ? `三条${label(p[0]!)}` : `Three of a kind ${label(p[0]!)}`
    case HandCategory.Straight:
      if (d.isWheel) return zh ? `顺子，${label(p[0]!)} 高（A-${label(p[0]!)}）` : `Straight, ${label(p[0]!)} high (A-${label(p[0]!)})`
      return zh ? `顺子，${label(p[0]!)} 高` : `Straight, ${label(p[0]!)} high`
    case HandCategory.Flush:
      return zh ? `同花，${label(p[0]!)} 高` : `Flush, ${label(p[0]!)} high`
    case HandCategory.FullHouse:
      return zh
        ? `葫芦，${label(p[0]!)} 带 ${label(p[1]!)}`
        : `Full house, ${label(p[0]!)} full of ${label(p[1]!)}`
    case HandCategory.Quads:
      return zh ? `四条${label(p[0]!)}` : `Four of a kind ${label(p[0]!)}`
    case HandCategory.StraightFlush:
      if (d.isRoyal) return zh ? '皇家同花顺' : 'Royal flush'
      if (d.isWheel) return zh ? `同花顺，${label(p[0]!)} 高（A-${label(p[0]!)}）` : `Straight flush, ${label(p[0]!)} high (A-${label(p[0]!)})`
      return zh ? `同花顺，${label(p[0]!)} 高` : `Straight flush, ${label(p[0]!)} high`
  }
}

/**
 * 哪些牌型的文案要带踢脚。
 *
 * 同花刻意不带：五张牌点数全部参与破平，逐张念出来只会淹没「A 高同花」这个重点；
 * §4.3 的模板也只给了 `同花，{r1} 高`。踢脚仍然在 `HandDescriptor.kickers` 里，
 * 需要展开的 UI 自己取。
 */
const SHOWS_KICKERS: readonly boolean[] = [
  true, //  HighCard
  true, //  Pair
  true, //  TwoPair
  true, //  Trips
  false, // Straight
  false, // Flush
  false, // FullHouse
  true, //  Quads
  false, // StraightFlush
]

export function formatHand(rank: HandRank, locale: Locale = 'zh-CN'): string {
  const head = formatHandShort(rank, locale)
  const d = describeHand(rank)
  if (!SHOWS_KICKERS[d.category] || d.kickers.length === 0) return head
  return locale === 'en'
    ? `${head}, kicker ${labels(d.kickers)}`
    : `${head}，踢脚 ${labels(d.kickers)}`
}
